import React, { useRef, useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Tooltip,
  Label,
  CommonSeriesSettings,
  Export,
  Title,
  Font,
} from 'devextreme-react/chart';
import { useFafsaData } from '../contexts';
import { FafsaFilterControls } from './FafsaFilterControls';
import { DashboardCard } from '../components/DashboardCard';
import {
  ChartDataPoint,
  GroupedChartDataPoint,
  ChartTooltipArg
} from '../types/FafsaDashboardData';

const FafsaChartContent = ({ chartRef }: { chartRef: React.RefObject<Chart> }) => {
  const { data, loading, error, filter } = useFafsaData();

  // Consistent structure regardless of grouping mode
  const { chartData, schools, maxValue } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], schools: [], maxValue: 100 };
    }

    const maxValRaw = Math.max(...data.map(d => d.percentageSubmitted));
    const calculatedMax = maxValRaw < 1 ? 5 : maxValRaw < 10 ? Math.ceil(maxValRaw + 5) : 100;

    if (!filter.groupBySchool) {
      // Simple View Data
      const mapped = data.map(item => ({
        year: `${item.year}-${item.year + 1}`,
        percentage: item.percentageSubmitted,
        label: `${item.submittedCount}/${item.totalEnrollment}`,
      }));
      return { chartData: mapped, schools: [], maxValue: calculatedMax };
    } 
    
    // Grouped View Data
    const years = [...new Set(data.map(d => d.year))].sort();
    const uniqueSchools = [...new Set(data.map(d => d.schoolName).filter((s): s is string => !!s))].sort();
    
    const grouped = years.map(year => {
      const yearData: GroupedChartDataPoint = { year: `${year}-${year + 1}` };
      uniqueSchools.forEach(school => {
        const item = data.find(d => d.year === year && d.schoolName === school);
        if (item) yearData[school] = item.percentageSubmitted;
      });
      return yearData;
    });

    return { chartData: grouped, schools: uniqueSchools, maxValue: calculatedMax };
  }, [data, filter.groupBySchool]);

  // Export Metadata
  const exportMetadata = useMemo(() => {
    const yearsLabel = filter.schoolYears.length > 0
      ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
      : 'All Years';
    const groupingLabel = filter.groupBySchool ? ' (Grouped by School)' : '';
    
    return {
      title: 'FAFSA Submission Rates',
      subtitle: `Years: ${yearsLabel}${groupingLabel}`
    };
  }, [filter.schoolYears, filter.groupBySchool]);

  if (loading) return <Skeleton variant="rounded" height={400} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <FafsaFilterControls />

      {error ? (
        <Typography color="error" align="center">{error}</Typography>
      ) : !chartData.length ? (
        <Typography align="center" color="textSecondary">
          No FAFSA data available for selected years
        </Typography>
      ) : (
        <>
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <Chart
              ref={chartRef}
              dataSource={chartData}
              height="100%"
            >
              <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                <Font size={18} weight={600} />
              </Title>
              <CommonSeriesSettings barPadding={0.3} />
              <ArgumentAxis />
              <ValueAxis
                visualRange={{ startValue: 0, endValue: maxValue }}
                tickInterval={maxValue <= 10 ? 1 : 10}
                title="Submission Rate (%)"
              />
              <Legend 
                visible={filter.groupBySchool} 
                horizontalAlignment="center" 
                verticalAlignment="bottom" 
              />
              <Tooltip
                enabled={true}
                customizeTooltip={(arg: ChartTooltipArg) => {
                   //Tooltip Logic
                   const year = parseInt(arg.argument.split('-')[0]);
                   // In simple mode, arg.seriesName is fixed; in grouped, it's the school name
                   const schoolName = filter.groupBySchool ? arg.seriesName : undefined; 

                   const item = data.find(d => 
                     d.year === year && 
                     (schoolName ? d.schoolName === schoolName : true)
                   );

                   const label = item ? `(${item.submittedCount}/${item.totalEnrollment} students)` : '';
                   return { text: `${arg.seriesName}: ${arg.value?.toFixed(1)}% ${label}` };
                }}
              />

              {!filter.groupBySchool ? (
                // Simple Series
                <Series
                  type="bar"
                  valueField="percentage"
                  argumentField="year"
                  name="FAFSA Submission Rate"
                  color="#4CAF50"
                  barWidth={100}
                >
                  <Label
                    visible={true}
                    position="outside"
                    customizeText={(arg: { value: number }) => `${arg.value.toFixed(1)}%`}
                  />
                </Series>
              ) : (
                // Grouped Series
                schools.map(school => (
                  <Series
                    key={school}
                    type="bar"
                    valueField={school}
                    argumentField="year"
                    name={school}
                  >
                    <Label
                      visible={true}
                      position="outside"
                      customizeText={(arg: { value: number }) => `${arg.value.toFixed(1)}%`}
                    />
                  </Series>
                ))
              )}
              
              <Export enabled={true} />
            </Chart>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Note: Counts and submission rates are calculated using 12th grade students only
          </Typography>
        </>
      )}
    </Box>
  );
};

export const FafsaChartCard = () => {
  const { filter } = useFafsaData();
  const chartRef = useRef<Chart>(null);

  const handleExport = () => {
    if (chartRef.current) {
      const yearsLabel = filter.schoolYears.length > 0 ? filter.schoolYears.join('_') : 'AllYears';
      const groupingLabel = filter.groupBySchool ? '_BySchool' : '';
      chartRef.current.instance.exportTo(`FAFSA_${yearsLabel}${groupingLabel}`, 'png');
    }
  };

  return (
    <DashboardCard title="FAFSA" onExport={handleExport}>
      <FafsaChartContent chartRef={chartRef} />
    </DashboardCard>
  );
};